import { useCallback, useMemo, useState } from 'react';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { SelectOption } from '@cbhq/cds-web/controls';
import { Dropdown } from '@cbhq/cds-web/dropdown';
import { HStack, VStack } from '@cbhq/cds-web/layout';

import { useAppState } from ':cloud/contexts/AppStateContext';
import { usePaymasterContext } from ':cloud/contexts/PaymasterProvider';
import { useDynamicHeight } from ':cloud/hooks/useDynamicHeight';
import { useFindOrCreateToken } from ':cloud/hooks/useFindOrCreateToken';
import { EventName, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { useGetPaymasterLogs } from ':cloud/queries/Base/useGetPaymasterLogs';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';

import { getISOFormattedTimeAgo } from '../helpers';

import { BaseNetworkDropdown } from './BaseNetworkDropdown';
import { logTimelineOptions, logTypeOptions } from './constants';
import { PaymasterLogDetail } from './PaymasterLogDetail';
import { PaymasterLogsTable } from './PaymasterLogsTable';

const logTypeContent = logTypeOptions.map((option) => (
  <SelectOption key={option.value} value={option.label} title={option.label} />
));

const logTimelineContent = logTimelineOptions.map((option) => (
  <SelectOption key={option.value} value={option.label} title={option.label} />
));

type PaymasterLogsProps = {
  onExportPress: () => void;
};

export function PaymasterLogs({ onExportPress }: PaymasterLogsProps) {
  const { activeOrg } = useGetUser();
  const { selectedProject } = useAppState();
  const { token } = useFindOrCreateToken(
    activeOrg?.organizationId,
    selectedProject?.id,
    selectedProject?.isTestnet,
  );

  const {
    selectedLogType: selectedType,
    selectedLogNetwork: selectedNetwork,
    selectedLogTimeline: selectedTimeline,
    setSelectedLogType: setSelectedType,
    setSelectedLogNetwork: setSelectedNetwork,
    setSelectedLogTimeline: setSelectedTimeline,
  } = usePaymasterContext();

  const [selectedLogIndex, setSelectedLogIndex] = useState(0);
  const [endtime, setEndtime] = useState(new Date().toISOString());

  // gets appropriate height of table section based on screen height
  // so that table scrolls but rest of content remains static
  const height = useDynamicHeight(201);

  const policyCreationDate = useMemo(() => {
    return token?.createTime;
  }, [token?.createTime]);

  const starttime = useMemo(() => {
    if (selectedTimeline === 'Past 30 min') {
      return getISOFormattedTimeAgo(30, 'minutes');
    }
    if (selectedTimeline === 'Past hour') {
      return getISOFormattedTimeAgo(1, 'hours');
    }
    if (selectedTimeline === 'Past day') {
      return getISOFormattedTimeAgo(1, 'days');
    }
    return policyCreationDate;
  }, [policyCreationDate, selectedTimeline]);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useGetPaymasterLogs({
    organizationId: activeOrg?.organizationId,
    projectId: selectedProject?.id,
    starttime,
    endtime,
    service: selectedType,
    network: selectedNetwork === 'Base Mainnet' ? 'base' : 'base-sepolia',
    cursor: '',
  });

  const logs = useMemo(() => {
    return data?.pages?.[0] ? data?.pages.flat() : [];
  }, [data]);

  const selectedLog = useMemo(() => {
    return logs?.[selectedLogIndex];
  }, [logs, selectedLogIndex]);

  const handleRefreshPress = useCallback(() => {
    setEndtime(new Date().toISOString());
  }, []);

  const handleNetworkChange = useCallback(
    (network: string) => {
      setSelectedNetwork(network);
      setSelectedLogIndex(0);
    },
    [setSelectedNetwork],
  );

  const handleTypeChange = useCallback(
    (type: string) => {
      setSelectedType(type);
      setSelectedLogIndex(0);
    },
    [setSelectedType],
  );

  const handleTimelineChange = useCallback(
    (timeline: string) => {
      setSelectedTimeline(timeline);
      setSelectedLogIndex(0);
    },
    [setSelectedTimeline],
  );

  useLogViewOnMount(EventName.paymaster_logs_page_view);

  return (
    <VStack flexGrow={1}>
      <HStack
        spacingVertical={3}
        spacingHorizontal={7}
        borderedBottom
        justifyContent="space-between"
      >
        <HStack gap={2}>
          <Dropdown
            value={selectedType}
            onChange={handleTypeChange}
            content={logTypeContent}
            enableMobileModal
          >
            <Button compact variant="secondary" endIcon="caretDown">
              {selectedType}
            </Button>
          </Dropdown>
          <BaseNetworkDropdown
            selectedNetwork={selectedNetwork}
            setSelectedNetwork={handleNetworkChange}
          />
          <Dropdown
            value={selectedTimeline}
            onChange={handleTimelineChange}
            content={logTimelineContent}
            enableMobileModal
          >
            <Button compact variant="secondary" endIcon="caretDown">
              {selectedTimeline}
            </Button>
          </Dropdown>
        </HStack>
        <HStack gap={2}>
          <IconButton name="refresh" onPress={handleRefreshPress} />
          <Button variant="secondary" onPress={onExportPress} compact>
            Export User Op logs
          </Button>
        </HStack>
      </HStack>
      <HStack flexGrow={1} height={height}>
        <PaymasterLogsTable
          onRowPress={setSelectedLogIndex}
          selectedLogIndex={selectedLogIndex}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={!!hasNextPage}
          isLoading={isLoading}
          data={data}
          handleRefreshPress={handleRefreshPress}
        />
        {selectedLog && (
          <PaymasterLogDetail
            log={selectedLog}
            selectedLogIndex={selectedLogIndex}
            setSelectedLogIndex={setSelectedLogIndex}
            maxLogIndex={logs?.length || 0}
            maxHeight={height}
          />
        )}
      </HStack>
    </VStack>
  );
}
